import type {
  AttachmentsPart,
  FeedbackDraftView,
  MemoryProposalView,
  ModelPartData,
  RevisionRequestPart,
  TaskProposalView,
  WikiChangesCard,
  WikiResolutionPart,
  WikiSuggestionView,
} from "@esi/shared";
import {
  ASK_USER_PART,
  type AskUserInput,
  askedQuestionOf,
  attachmentsPartSchema,
  type CitationsData,
  isToolPart,
  PRODUCT_HELP_TOOL_NAME,
  type QuestionAnswerData,
  REVISION_REQUEST_PART,
  type RenderedCitation,
  SPEND_LIMIT_PART,
  stepsOf,
  type ToolApprovalsData,
  toolNameOf,
  type UnsourcedRange,
  type WebCitation,
  WIKI_WRITE_TOOL,
  wikiSlug,
} from "@esi/shared";
import type { UIMessage } from "ai";
import { Bot, EyeOff } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MarkdownBlocks, type RenderOptions } from "@/components/markdown/Markdown";
import { parseMarkdown } from "@/components/markdown/parse";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { openHrefOf, useSourceViewer } from "../projects/SourceViewer.tsx";
import { useOpenWiki, wikiPath } from "../router.tsx";
import { isAdmin, useOptionalSession } from "../session.tsx";
import { markFound } from "../settings-shell/setting-anchors.ts";
import { AudienceLine, GeneralAnswerNote, WebResultsNote } from "../trust/AudienceLine.tsx";
import { answerModels, ModelMark } from "../trust/ModelNote.tsx";
import { parseInstructions } from "../wiki/instructions.ts";
import { useWikiIndex } from "../wiki/queries.ts";
import { ResolutionSentLine } from "../wiki/ResolutionRunCard.tsx";
import { RevisionSentLine } from "../wiki/RevisionRequestCard.tsx";
import { type ResolutionRequestData, resolutionOf } from "../wiki/resolve.ts";
import { ArtefactCard } from "./artefacts/ArtefactCard.tsx";
import { artefactCardOf, isArtefactToolPart } from "./artefacts/parts.ts";
import type { ConversationScope } from "./artefacts/queries.ts";
import { MessageAttachments, useAttachmentContext } from "./attachments/AttachmentControls.tsx";
import {
  CitationChip,
  CitationGroupChip,
  type PassageRef,
  StateIcon,
  stateTextClass,
  WebBadge,
  webMeta,
} from "./CitationChip.tsx";
import { CodeRunFiles } from "./CodeRun.tsx";
import { useCitationOpen } from "./citation-open.ts";
import {
  authorizedAttachmentCitation,
  type ChipPosition,
  citationHref,
  citationState,
  occurrenceState,
  stateText,
} from "./citation-state.ts";
import {
  blankBefore,
  blockAtoms,
  citationsOf,
  locationLabel,
  markUnverified,
  planRuns,
  provenanceLabel,
  type Token,
  tokenize,
  tokenOffsets,
  unverifiedStatements,
} from "./citations.ts";
import { ClassificationMark } from "./classification.tsx";
import { codeRunResultOf, isRunCodePart, ranCode } from "./code-run.ts";
import { HelpCitationChip } from "./HelpCitationChip.tsx";
import { HelpSearchRow } from "./HelpSearchRow.tsx";
import { helpTarget } from "./help-screens.ts";
import { QuestionCard } from "./QuestionCard.tsx";
import { Timeline } from "./Timeline.tsx";
import { ToolActivity } from "./ToolActivity.tsx";
import {
  approvalIdOf,
  approvalsOf,
  type DecisionFailure,
  isOutboundToolPart,
  safeHref,
  type ToolPart,
} from "./tool-approvals.ts";
import { WikiWriteRow } from "./WikiWriteRow.tsx";
import {
  includesWebResults,
  isHelpHandle,
  isWebHandle,
  webCitationState,
  webSnippetsOf,
} from "./web-results.ts";

export { type CitationState, citationState } from "./citation-state.ts";

/**
 * A chat message as the panel holds it: `hidden` and `createdAt` come from the server's rendered
 * message (a message streamed in this session has neither).
 */
export type ChatMessage = UIMessage<
  { hidden?: boolean; createdAt?: string },
  {
    citations: CitationsData;
    attachments: AttachmentsPart["data"];
    "tool-approvals": ToolApprovalsData;
    // The turn stopped at the hard monthly spend limit (stored with the message).
    "spend-limit": {
      reason: "model_budget" | "turn_budget" | "user_budget" | "unavailable" | "timeout";
    };
    // A memory the assistant suggests after this answer (memory-personal); never stored in it.
    "memory-proposal": MemoryProposalView;
    // A task change the assistant proposed in this answer (agent-turn D11); one part each.
    "task-proposal": TaskProposalView;
    // A feedback draft under the answer that drafted it (feedback-from-conversation).
    "feedback-draft": FeedbackDraftView;
    // A wiki page the assistant suggests after this answer (wiki-suggestions); never stored in it.
    "wiki-suggestion": WikiSuggestionView;
    // The working change set's card (wiki-collaboration D8; stored with the message).
    "wiki-changes": WikiChangesCard;
    // A Revise turn's request (wiki-collaboration D15; stored with its user message).
    "wiki-revision": RevisionRequestPart["data"];
    // A resolution run's turn (wiki-resolve-with-agent D2; stored with its user message), and the
    // client's marker on the optimistic message before the stored one replaces it.
    "wiki-resolution": WikiResolutionPart["data"];
    "wiki-resolution-request": ResolutionRequestData;
    // The models that wrote it (model-policy-and-transparency; stored with the message).
    model: ModelPartData;
    // The question a user message answers (agent-turn D7; set by the server, stored with it).
    "question-answer": QuestionAnswerData;
  }
>;

export interface MessageViewProps {
  message: ChatMessage;
  projectId: string;
  /** Enables the passage preview (read per request); absent where there is no stored message. */
  conversationId?: string;
  /** Whether the viewer may download this document (its classification allows it). */
  canOpen: (documentId: string) => boolean;
  /** This message is still being written. */
  streaming?: boolean;
  /** Who can read the conversation, worded for the viewer (shown under answers). */
  audience?: { text: string; restricted: boolean } | null;
  /** Actions under an assistant message (save as file, save to wiki). */
  actions?: ReactNode;
  /**
   * Proposal cards of this answer (task and memory, agent-turn D11, D13): inside the answer,
   * above its sources and footer, like the question card, so the footer stays the last line.
   */
  cards?: ReactNode;
  /**
   * Answers a pending outbound call (allow or decline). Given only to a viewer who may decide
   * (the conversation's creator, project not frozen); others see the waiting row.
   */
  onToolDecision?: (approvalId: string, approved: boolean) => void;
  /** Decisions the server refused, by approval ID. */
  toolFailures?: ReadonlyMap<string, DecisionFailure>;
  /** Another reply is streaming in this thread: decisions wait for it. */
  busy?: boolean;
  /** A later message follows this one: its waiting tool requests expired (as on the server). */
  superseded?: boolean;
  /** The conversation the artefact cards open from (absent: cards cannot open). */
  artefacts?: ConversationScope | null;
  /**
   * Clarifying questions (agent-turn D8): the choice each question's answer picked, by tool
   * call; the pick and "Answer in your own words" for the one the question asks.
   */
  questions?: {
    answers: ReadonlyMap<string, number>;
    mine: boolean;
    onPick?: (toolCallId: string, choice: string, index: number) => void;
    onOwnWords?: () => void;
    /** A message is being checked or sent: picks wait. */
    sending?: boolean;
    /** False once a later message follows this one (a failed send aside): nothing is asked. */
    open?: boolean;
  };
}

export function MessageView(props: MessageViewProps) {
  const { message, projectId, conversationId, canOpen, streaming, audience, actions } = props;
  // `[[Title]]` in an answer opens the readable page beside the conversation (D8a); the index
  // is read only when the answer names a page, and shared with the knowledge panel's.
  const namesPage =
    projectId !== "" &&
    message.role === "assistant" &&
    message.parts.some((p) => p.type === "text" && p.text.includes("[["));
  const index = useWikiIndex(projectId, namesPage);
  const { show: showWiki } = useOpenWiki();
  const wikiLinks = useMemo(() => {
    if (!namesPage || !index.data) {
      return undefined;
    }
    const bySlug = new Map(index.data.pages.map((p) => [wikiSlug(p.title), p.id]));
    return {
      resolve: (title: string) => {
        const id = bySlug.get(wikiSlug(title));
        return id ? wikiPath(projectId, id) : null;
      },
      ...(conversationId && {
        openWikiLink: (href: string) => {
          const pageId = /\/wiki\/([0-9a-f-]{36})/i.exec(href)?.[1];
          if (pageId) {
            showWiki({ kind: "page", pageId: pageId.toLowerCase(), version: null });
          }
        },
      }),
    };
  }, [namesPage, index.data, projectId, conversationId, showWiki]);
  const session = useOptionalSession();
  const { t } = useTranslation();
  // Parsed once per parts array: a finished answer keeps the same object while others stream.
  const attachmentContext = useAttachmentContext();
  const data = useMemo(() => {
    const data = citationsOf(message.parts);
    return data
      ? {
          ...data,
          citations: data.citations.map((c) =>
            authorizedAttachmentCitation(c, attachmentContext?.list),
          ),
        }
      : null;
  }, [message.parts, attachmentContext?.list]);
  const approvals = useMemo(() => approvalsOf(message.parts), [message.parts]);
  // Code ran for this answer (agent-turn D20): the audience line says so.
  const ran = useMemo(() => ranCode(message.parts), [message.parts]);
  // The step timeline (agent-turn D4): absent from answers stored before it.
  const steps = useMemo(() => stepsOf(message.parts), [message.parts]);
  const web = useMemo(
    () => ({
      included: includesWebResults(message.parts),
      snippets: webSnippetsOf(message.parts),
    }),
    [message.parts],
  );
  // The sources list, folded under the answer until asked for (per message, in memory only),
  // and the row "Show in sources" moves to (`n` makes each ask a new one).
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [reveal, setReveal] = useState<{ handle: string; n: number } | null>(null);
  if (message.metadata?.hidden) {
    return (
      <article
        data-esi-content="messages"
        aria-label={t("citations.speaker.assistant")}
        className="flex items-start gap-2.5 rounded-lg border border-line bg-surface-sunken px-3.5 py-3 text-sm"
      >
        <EyeOff
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <div className="flex flex-col gap-0.5">
          <p className="font-medium text-fg">{t("citations.hiddenNotice")}</p>
          <p className="text-fg-secondary">{t("citations.hiddenExplained")}</p>
        </div>
      </article>
    );
  }
  const texts = message.parts.flatMap((p) => (p.type === "text" ? [p.text] : []));
  const revision = message.parts.find(
    (p): p is { type: typeof REVISION_REQUEST_PART; data: RevisionRequestPart["data"] } =>
      p.type === REVISION_REQUEST_PART,
  );
  const resolution = resolutionOf(message);
  if (message.role === "user" && resolution) {
    // The server composed this message from the reviewers' threads (fenced words): it reads as
    // one line, the words staying in their threads; the run card follows the turn.
    return (
      <ResolutionSentLine
        projectId={projectId}
        changeSetId={resolution.changeSetId}
        threadCount={resolution.threadCount}
        {...(resolution.title !== undefined && { title: resolution.title })}
      />
    );
  }
  if (message.role === "user" && revision) {
    // The server composed this message from a reviewer's request: it reads as the card's
    // settled line, the reviewer's words staying in the thread.
    return <RevisionSentLine data={revision.data} />;
  }
  const instructions = message.role === "user" ? parseInstructions(texts.join("\n\n")) : null;
  if (instructions) {
    // Instructions sent from the wiki preview's tray (wiki-collaboration-ux D3): the passages
    // as quotes, each with its instruction; the agent receives the plain message.
    return <InstructionsMessage instructions={instructions} />;
  }
  if (message.role === "user") {
    return (
      <article
        data-esi-content="messages"
        aria-label={t("citations.speaker.user")}
        className="max-w-[85%] self-end rounded-2xl rounded-br-md bg-surface-selected/70 px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap text-fg"
      >
        {texts.join("\n\n")}
        {message.parts.flatMap((p) => {
          const part = attachmentsPartSchema.safeParse(p);
          return part.success
            ? [
                <MessageAttachments
                  key={part.data.data.attachments.map((a) => a.documentId).join(":")}
                  part={part.data}
                />,
              ]
            : [];
        })}
      </article>
    );
  }

  const byHandle = new Map(data?.citations.map((c) => [c.handle, c]) ?? []);
  const webByHandle = new Map(data?.web.map((c) => [c.handle, c]) ?? []);
  // Help citations (`H#`): absent from a server one release behind.
  const helpByHandle = new Map(data?.help?.map((c) => [c.handle, c]) ?? []);
  const passage: PassageRef | null = conversationId
    ? { projectId, conversationId, messageId: message.id }
    : null;
  // Unsourced marks only mean something next to sources: an answer that cites no resolved source
  // (knowledge or web) and used no web results gets no inline marks, and once finished one
  // general-answer note. With web results in the turn, "Includes web results" must not stand
  // alone over general statements, so they keep their marks. A finished answer whose citations
  // part never came keeps its markers too: nothing else would signal them.
  const sourced =
    data === null
      ? !streaming
      : web.included || data.citations.length > 0 || (data.help?.length ?? 0) > 0;
  const general = !streaming && !sourced && (data?.unsourced.length ?? 0) > 0;
  // Text and outbound tool calls in the order they happened; internal (knowledge) tools are not
  // shown. `k` counts the non-blank text parts only (blank ones are never stored): it is the
  // `textPart` of their unsourced ranges.
  const items: (
    | { kind: "text"; text: string; k: number }
    | { kind: "tool"; part: ToolPart }
    | { kind: "help"; part: ToolPart }
    | { kind: "wiki"; part: ToolPart }
    | { kind: "artefact"; part: unknown }
    | { kind: "question"; part: unknown; toolCallId: string; question: AskUserInput }
  )[] = [];
  let k = 0;
  for (const part of message.parts) {
    if (part.type === "text") {
      if (part.text.trim() !== "") {
        items.push({ kind: "text", text: part.text, k: k++ });
      }
    } else if (part.type === ASK_USER_PART) {
      // A clarifying question: its card, in the answer (never in the timeline's step row); a
      // call that showed nothing (invalid, a second one) has none.
      const asked = askedQuestionOf(part);
      if (asked) {
        items.push({ kind: "question", part, ...asked, question: asked.input });
      }
    } else if (isArtefactToolPart(part)) {
      // An artefact the agent wrote: its card, never a tool row with the content as arguments.
      items.push({ kind: "artefact", part });
    } else if (isOutboundToolPart(part)) {
      items.push({ kind: "tool", part });
    } else if (isToolPart(part) && toolNameOf(part) === PRODUCT_HELP_TOOL_NAME) {
      // Internal, but shown: the query and the help pages it found (agent-tools).
      items.push({ kind: "help", part: part as ToolPart });
    } else if (isToolPart(part) && toolNameOf(part) === WIKI_WRITE_TOOL) {
      // Internal, but shown without content: what the agent wrote into the change set.
      items.push({ kind: "wiki", part: part as ToolPart });
    }
  }
  // With a timeline, each tool row moves into its step's row (D4); a call waiting for the
  // user's decision stays above the answer, as does any call no step names.
  const stepCalls = new Set(steps.flatMap((s) => s.calls.map((c) => c.toolCallId)));
  const callIdOf = (part: unknown) => {
    const id = (part as { toolCallId?: unknown } | null)?.toolCallId;
    return typeof id === "string" ? id : undefined;
  };
  const embeddedHere = (item: (typeof items)[number]) => {
    if (item.kind === "text" || item.kind === "question") {
      return false;
    }
    const id = callIdOf(item.part);
    return (
      id !== undefined &&
      stepCalls.has(id) &&
      (item.part as { state?: unknown }).state !== "approval-requested"
    );
  };
  const showInSources = (handle: string) => {
    setSourcesOpen(true);
    setReveal((r) => ({ handle, n: (r?.n ?? 0) + 1 }));
  };
  // A chip in the text shows its citation's verdict in that statement (`at`); the sources list,
  // its source's own state.
  const stateOf = (h: string, at?: ChipPosition) =>
    occurrenceState(
      isHelpHandle(h)
        ? webCitationState(helpByHandle.get(h), data)
        : isWebHandle(h)
          ? webCitationState(webByHandle.get(h), data)
          : citationState(byHandle.get(h), data),
      data,
      h,
      at,
    );
  const chip = (
    h: string,
    key: string,
    titled = false,
    before?: () => void,
    at?: ChipPosition,
  ): ReactNode => {
    const show = (handle: string) => {
      before?.();
      showInSources(handle);
    };
    if (isHelpHandle(h)) {
      return (
        <HelpCitationChip
          key={key}
          handle={h}
          help={helpByHandle.get(h)}
          state={stateOf(h, at)}
          passage={passage}
          target={helpTarget(helpByHandle.get(h)?.screen, {
            projectId: projectId || null,
            admin: session ? isAdmin(session.roles) : false,
          })}
        />
      );
    }
    return isWebHandle(h) ? (
      <CitationChip
        key={key}
        handle={h}
        citation={undefined}
        web={webByHandle.get(h)}
        webSnippet={web.snippets.get(h) ?? null}
        data={data}
        state={stateOf(h, at)}
        passage={passage}
        canOpen={canOpen}
        titled={titled}
        onShowInSources={data ? show : undefined}
      />
    ) : (
      <CitationChip
        key={key}
        handle={h}
        citation={byHandle.get(h)}
        data={data}
        state={stateOf(h, at)}
        passage={passage}
        canOpen={canOpen}
        titled={titled}
        onShowInSources={data && byHandle.has(h) ? show : undefined}
      />
    );
  };
  // A run's known handles, once each: chips side by side, or one group chip beyond three.
  const renderRun = (handles: readonly string[], key: string, at?: ChipPosition): ReactNode =>
    handles.length > GROUP_ABOVE ? (
      <CitationGroupChip
        key={key}
        sources={handles.map((h, i) => ({
          handle: h,
          title: isHelpHandle(h)
            ? (helpByHandle.get(h)?.title ?? null)
            : isWebHandle(h)
              ? (webByHandle.get(h)?.title ?? webByHandle.get(h)?.domain ?? null)
              : (byHandle.get(h)?.title ?? null),
          state: stateOf(h, at),
          chip: (close) => chip(h, `${key}-${i}`, false, close, at),
        }))}
      />
    ) : (
      handles.map((h, i) => chip(h, `${key}-${i}`, false, undefined, at))
    );
  const known = (h: string) =>
    !data || byHandle.has(h) || webByHandle.has(h) || helpByHandle.has(h);
  const render = (
    token: Exclude<Token, { kind: "text" }>,
    key: string,
    at?: ChipPosition,
  ): ReactNode => {
    switch (token.kind) {
      case "unsourced-marker":
        return sourced ? <UnsourcedMarker key={key} /> : null;
      case "cite": {
        // Once the citations part is in, a handle it does not list is no citation: plain text.
        const handles = [...new Set(token.handles.filter(known))];
        return handles.length === 0 ? token.raw : renderRun(handles, key, at);
      }
    }
  };
  /** `inStep`: the row is built inside the timeline (its files are then named, not shown). */
  const renderItem = (item: (typeof items)[number], i: number, inStep = false): ReactNode =>
    item.kind === "text" ? (
      <AnswerText
        key={`t${i}`}
        text={item.text}
        textPart={item.k}
        sourced={sourced}
        ranges={sourced && data ? data.unsourced.filter((r) => r.textPart === item.k) : NO_RANGES}
        webRanges={(data?.fromWeb ?? []).filter((r) => r.textPart === item.k)}
        data={data}
        known={known}
        stateAt={(h, offset) => stateOf(h, { textPart: item.k, offset })}
        renderAtom={render}
        renderRun={renderRun}
        wikiLinks={wikiLinks}
      />
    ) : item.kind === "help" ? (
      <HelpSearchRow key={`h${i}`} part={item.part} />
    ) : item.kind === "wiki" ? (
      <WikiWriteRow key={`w${i}`} part={item.part} />
    ) : item.kind === "question" ? (
      <QuestionCard
        key={`q${i}`}
        question={item.question}
        answered={props.questions?.answers.get(item.toolCallId)}
        mine={props.questions?.mine === true}
        busy={props.busy === true || streaming === true || props.questions?.sending === true}
        // Only the latest answer's question is open: a later message answered it or moved on.
        open={props.questions?.open !== false}
        onPick={
          props.questions?.onPick && props.questions.open !== false
            ? (choice, index) => props.questions?.onPick?.(item.toolCallId, choice, index)
            : undefined
        }
        onOwnWords={props.questions?.onOwnWords}
      />
    ) : item.kind === "artefact" ? (
      <ArtefactCard
        key={`a${i}`}
        card={artefactCardOf(item.part)}
        scope={props.artefacts ?? null}
        streaming={streaming === true}
      />
    ) : (
      <ToolActivity
        key={`c${i}`}
        part={item.part}
        approvals={approvals}
        failure={toolFailureOf(props.toolFailures, item.part)}
        onDecide={streaming || props.superseded ? undefined : props.onToolDecision}
        busy={props.busy}
        superseded={props.superseded}
        at={message.metadata?.createdAt}
        scope={props.artefacts ?? null}
        inStep={inStep}
      />
    );
  // A tool call's index among the items: its row is built only when the timeline is open.
  const embedAt = new Map<string, number>();
  items.forEach((item, i) => {
    const id = item.kind === "text" ? undefined : callIdOf(item.part);
    if (id !== undefined && embeddedHere(item)) {
      embedAt.set(id, i);
    }
  });
  const renderEmbed = (toolCallId: string): ReactNode | undefined => {
    const i = embedAt.get(toolCallId);
    const item = i === undefined ? undefined : items[i];
    return item && i !== undefined ? renderItem(item, i, true) : undefined;
  };
  // The turn stopped between two steps: the organisation's hard monthly spend limit, or the
  // most one answer may spend (`models.turnMaxUsd`), or the user's monthly share of the limit.
  const stopPart = message.parts.find((p) => p.type === SPEND_LIMIT_PART) as
    | { data?: { reason?: string } }
    | undefined;
  const stoppedBy = stopPart
    ? stopPart.data?.reason === "unavailable"
      ? "stoppedUnavailable"
      : stopPart.data?.reason === "timeout"
        ? "stoppedByTime"
        : stopPart.data?.reason === "turn_budget"
          ? "stoppedByTurnSpend"
          : stopPart.data?.reason === "user_budget"
            ? "stoppedByUserShare"
            : "stoppedBySpend"
    : null;
  // Citations whose source the viewer cannot read: their number only, never what they are.
  const hidden = data?.citations.filter((c) => c.title === null && !c.deleted).length ?? 0;
  // The models that wrote it (stored with the message; a hidden message has no parts).
  const model = answerModels(message.parts);
  const modelMark = model ? (
    <>
      <span aria-hidden="true">·</span>
      <ModelMark names={model.names} reason={model.reason} />
    </>
  ) : null;

  return (
    <article
      data-esi-content="messages"
      aria-label={t("citations.speaker.assistant")}
      data-message-id={message.id}
      className="flex flex-col gap-3 text-[15px] text-fg"
    >
      {steps.length > 0 ? (
        <Timeline
          steps={steps}
          live={streaming === true}
          checking={data?.state === "checking"}
          renderEmbed={renderEmbed}
        />
      ) : streaming && items.length === 0 ? (
        // Before the turn's first step arrives: the live line alone (its progress).
        <Timeline steps={[]} live />
      ) : null}
      {/* Artefact cards also stay in the answer (D4 implementation note, 2026-11-02). */}
      {items.map((item, i) => {
        if (!embeddedHere(item) || item.kind === "artefact") {
          return renderItem(item, i);
        }
        // A code run's files stay in the answer too, like artefact cards (agent-turn D20).
        const files =
          item.kind !== "text" && isRunCodePart(item.part)
            ? codeRunResultOf((item.part as { output?: unknown }).output)?.files
            : undefined;
        return files && files.length > 0 ? (
          <CodeRunFiles
            key={`f${item.kind === "text" ? i : (callIdOf(item.part) ?? i)}`}
            files={files}
            scope={props.artefacts ?? null}
          />
        ) : null;
      })}
      {stoppedBy ? (
        <Notice tone="warning" live>
          {t(`citations.${stoppedBy}`)}
        </Notice>
      ) : null}
      {props.cards}
      {data &&
      (data.citations.length > 0 || data.web.length > 0 || (data.help?.length ?? 0) > 0) ? (
        <AnswerSources
          data={data}
          messageId={message.id}
          projectId={projectId}
          conversationId={attachmentContext?.conversationId}
          canOpen={canOpen}
          expanded={sourcesOpen}
          onExpandedChange={setSourcesOpen}
          reveal={reveal}
          chip={(h) => chip(h, h, true)}
        />
      ) : null}
      {!streaming && (audience || actions || web.included || general || model) ? (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          {audience ? (
            <AudienceLine
              text={audience.text}
              restricted={audience.restricted}
              hidden={hidden > 0 ? t("audience.hiddenSources", { count: hidden }) : null}
              webResults={web.included}
              ranCode={ran}
              general={general}
              generatedSources={
                data?.citations.some((c) => "generated" in c && c.generated === true) ?? false
              }
            >
              {modelMark}
            </AudienceLine>
          ) : web.included ? (
            <WebResultsNote>{modelMark}</WebResultsNote>
          ) : general ? (
            <GeneralAnswerNote>{modelMark}</GeneralAnswerNote>
          ) : model ? (
            <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
              <ModelMark names={model.names} reason={model.reason} />
            </p>
          ) : (
            <span />
          )}
          {actions}
        </div>
      ) : null}
    </article>
  );
}

/**
 * One text part of an answer as safe Markdown. Parsed once per text (memoised, so a finished
 * part is not re-parsed while later ones stream); citation handles and markers are atoms found
 * on the raw text, and the unsourced and unverified marks are raw-offset decorations, so
 * formatting never moves a mark onto other words.
 */
function AnswerText(props: {
  text: string;
  /** Its index among the message's non-blank text parts (`textPart` of the ranges). */
  textPart: number;
  /** The answer cites a source: markers show; otherwise they (and the blanks before them) go. */
  sourced: boolean;
  ranges: readonly UnsourcedRange[];
  webRanges: readonly UnsourcedRange[];
  data: CitationsData | null;
  /** Whether a handle is a citation (listed by the citations part, or any while it is not in). */
  known: (handle: string) => boolean;
  /** A handle's state at a raw offset of this part (per-statement verdicts). */
  stateAt: (handle: string, offset: number) => string;
  renderAtom: (token: Exclude<Token, { kind: "text" }>, key: string, at: ChipPosition) => ReactNode;
  renderRun: (handles: readonly string[], key: string, at: ChipPosition) => ReactNode;
  /** `[[Title]]` links to readable wiki pages, opened in the wiki panel (wiki-collaboration). */
  wikiLinks?: Pick<RenderOptions, "resolve" | "openWikiLink"> | undefined;
}) {
  const { text, textPart, sourced, ranges, webRanges, data, known, stateAt } = props;
  const { renderAtom, renderRun, wikiLinks } = props;
  const { t } = useTranslation();
  const parsed = useMemo(() => {
    const { atoms } = tokenOffsets(tokenize(text, []));
    // The blank run before each marker, hidden with the marker in a general answer, so
    // "offered [unsourced]." reads "offered.". Decorations only touch text, never code. A marker
    // glued to the next word keeps its blank: "machines [unsourced]that" reads "machines that".
    const blanks = atoms.flatMap((a) => {
      const blank = a.token.kind === "unsourced-marker" ? blankBefore(text, a.start, a.end) : null;
      return blank ? [{ ...blank, kind: "hidden" as const }] : [];
    });
    const blocks = parseMarkdown(text, { atoms: new Map(atoms.map((a) => [a.start, a.end])) });
    return {
      blocks,
      atomList: atoms,
      inBlocks: blockAtoms(blocks),
      atoms: new Map(atoms.map((a) => [a.start, a.token])),
      blanks,
    };
  }, [text]);
  // Runs grouped and repeats dropped (rendering only), again when the known handles change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `known` and `stateAt` follow `data`
  const runs = useMemo(
    () => planRuns(text, parsed.atomList, parsed.inBlocks, known, stateAt),
    [text, parsed, data],
  );
  // Recomputed only when this part's text, ranges or citation verdicts change, so a finished
  // part does no work while a later one streams.
  const rangesKey = JSON.stringify([ranges, webRanges]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the ranges are keyed by their content
  const marks = useMemo(() => {
    if (!sourced) {
      return withHidden(parsed.blanks, runs.hidden);
    }
    // The check's verdicts per statement when the server sent them; else the handles' statuses.
    const underlined = unverifiedStatements(data, textPart, text, known);
    const tokens = underlined
      ? tokenize(text, ranges, webRanges, underlined)
      : markUnverified(tokenize(text, ranges, webRanges), unverifiedIn(data));
    return withHidden(tokenOffsets(tokens).marks, runs.hidden);
  }, [sourced, text, textPart, rangesKey, data, parsed, runs]);
  return (
    <MarkdownBlocks
      blocks={parsed.blocks}
      headingBase={3}
      showLinkHost
      {...wikiLinks}
      decorations={marks}
      renderAtom={(atom, key) => {
        const token = parsed.atoms.get(atom.start);
        const plan = runs.plan.get(atom.start);
        const at = { textPart, offset: atom.start };
        return plan?.kind === "hidden"
          ? null
          : plan?.kind === "run"
            ? renderRun(plan.handles, key, at)
            : token
              ? renderAtom(token, key, at)
              : atom.raw;
      }}
      renderDecoration={(kind, children, key, last) =>
        kind === "hidden" ? null : (
          <span
            key={key}
            className={
              // Unsourced is quiet and neutral (being general is no warning); unverified, a failed
              // check, is the one loud mark: solid amber, thicker.
              kind === "unsourced"
                ? "underline decoration-line-strong decoration-dotted decoration-1 underline-offset-4"
                : kind === "web"
                  ? "underline decoration-info-border/80 decoration-dotted decoration-1 underline-offset-4"
                  : "underline decoration-warning-border decoration-solid decoration-2 underline-offset-[5px]"
            }
          >
            {children}
            {last ? (
              <span className="sr-only">
                {" "}
                (
                {t(
                  kind === "unsourced"
                    ? "citations.unsourcedSpan"
                    : kind === "web"
                      ? "citations.web.span"
                      : "citations.unverifiedSpan",
                )}
                )
              </span>
            ) : null}
          </span>
        )
      }
    />
  );
}

const NO_RANGES: readonly UnsourcedRange[] = [];

/** A run citing more distinct sources than this renders as one group chip. */
const GROUP_ABOVE = 3;

/**
 * Adds the blanks of the chips not rendered as hidden decorations, first so they win. A mark
 * ending inside one ends before it instead, so its screen-reader text (on its last piece) stays.
 */
function withHidden<K extends string>(
  marks: readonly { start: number; end: number; kind: K }[],
  hidden: readonly { start: number; end: number }[],
): { start: number; end: number; kind: K | "hidden" }[] {
  if (hidden.length === 0) {
    return [...marks];
  }
  const trimmed = marks.flatMap((m) => {
    const gap = hidden.find((h) => m.end > h.start && m.end <= h.end);
    if (!gap) {
      return [m];
    }
    return gap.start > m.start ? [{ ...m, end: gap.start }] : [];
  });
  return [...hidden.map((h) => ({ ...h, kind: "hidden" as const })), ...trimmed];
}

/**
 * The end of an unsourced statement in a cited answer: a small neutral dot whose label shows on
 * hover. The statement already carries its screen-reader text, so the dot is visual only and
 * adds no tab stop. Its own delay: the sidebar's provider opens tooltips at once, and a dot
 * crossed on the way should not flash its label.
 */
function UnsourcedMarker() {
  const { t } = useTranslation();
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <span
          aria-hidden="true"
          data-mark="unsourced"
          className="inline-flex size-3.5 cursor-default items-center justify-center align-middle"
        >
          <span className="size-[5px] rounded-full bg-line-strong" />
        </span>
      </TooltipTrigger>
      <TooltipContent aria-hidden="true">{t("citations.unsourcedBadge")}</TooltipContent>
    </Tooltip>
  );
}

const toolFailureOf = (
  failures: ReadonlyMap<string, DecisionFailure> | undefined,
  part: ToolPart,
): DecisionFailure | undefined => {
  const id = approvalIdOf(part);
  return id ? failures?.get(id) : undefined;
};

/** A statement is unverified once the check is done and every handle closing it failed. */
function unverifiedIn(data: CitationsData | null) {
  const byHandle = new Map(data?.citations.map((c) => [c.handle, c]) ?? []);
  // Web and help citations share the same verdict rule.
  const statusOnly = new Map(
    [...(data?.web ?? []), ...(data?.help ?? [])].map((c) => [c.handle, c]),
  );
  const stateOf = (h: string) =>
    statusOnly.has(h)
      ? webCitationState(statusOnly.get(h), data)
      : citationState(byHandle.get(h), data);
  return (handles: string[]) => {
    const known = handles.filter((h) => byHandle.has(h) || statusOnly.has(h));
    return (
      data?.state === "done" && known.length > 0 && known.every((h) => stateOf(h) === "unsupported")
    );
  };
}

/** Source chips shown before `+N`: about two lines from `sm`, one on phones. */
const SOURCE_CHIPS = { wide: 5, narrow: 3 } as const;

/** A source row's anchor: per message, since every answer has its own `S1`. */
const sourceAnchor = (messageId: string, handle: string) => `source-${messageId}-${handle}`;

/** A source row as an anchor target: focusable, tinted with a focus outline when reached. */
const SOURCE_ROW_CLASS =
  "-mx-1.5 scroll-mt-6 rounded-sm px-1.5 transition-colors duration-(--duration-base) outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus focus-visible:outline-solid data-found:bg-surface-selected data-found:outline-2 data-found:outline-solid data-found:-outline-offset-2 data-found:outline-focus";

/**
 * The sources under an answer, always compact: a count line naming the unverified and
 * unavailable verdicts (and "Checking citations…" while checking), then one wrapping line of
 * source chips (project, then web) with `+N` for the rest. Show all expands the full rows in
 * place, project and web sections apart; the visible count is announced once per toggle.
 */
function AnswerSources(props: {
  conversationId: string | undefined;
  data: CitationsData;
  messageId: string;
  projectId: string;
  canOpen: (documentId: string) => boolean;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** The row to move to once expanded ("Show in sources"). */
  reveal: { handle: string; n: number } | null;
  /** A source's chip for the compact line (with its title). */
  chip: (handle: string) => ReactNode;
}) {
  const { data, messageId, projectId, canOpen, expanded, onExpandedChange, reveal } = props;
  const { t } = useTranslation();
  const wide = useMediaQuery("(min-width: 640px)");
  const [announce, setAnnounce] = useState("");
  const toggle = useRef<HTMLButtonElement>(null);
  const help = data.help ?? [];
  const handles = [
    ...data.citations.map((c) => c.handle),
    ...data.web.map((c) => c.handle),
    ...help.map((c) => c.handle),
  ];
  const total = handles.length;
  const room = wide ? SOURCE_CHIPS.wide : SOURCE_CHIPS.narrow;
  const visible = handles.slice(0, room);
  const folded = total - visible.length;
  const states = [
    ...data.citations.map((c) => citationState(c, data)),
    ...data.web.map((c) => webCitationState(c, data)),
    ...help.map((c) => webCitationState(c, data)),
  ];
  const unverified = states.filter((s) => s === "unsupported").length;
  const unavailable = states.filter((s) => s === "unavailable" || s === "deleted").length;
  const heading = `source-heading-${messageId}`;
  const setOpen = (next: boolean) => {
    onExpandedChange(next);
    // Worded apart, so a toggle with nothing folded is still announced.
    setAnnounce(
      next
        ? t("citations.showingAll", { count: total })
        : t("citations.showing", { count: visible.length }),
    );
  };
  // "Show in sources": once the rows are in, scroll to the asked one, focus it and mark it. Each
  // ask is handled once: expanding again later must not move to an old one.
  const revealed = useRef(0);
  useEffect(() => {
    if (!reveal || !expanded || reveal.n === revealed.current) {
      return;
    }
    revealed.current = reveal.n;
    // After the preview's own focus handling as it closes.
    const timer = window.setTimeout(() => {
      const row = document.getElementById(sourceAnchor(messageId, reveal.handle));
      if (row) {
        markFound(row, "nearest");
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [reveal, expanded, messageId]);
  return (
    <section className="flex min-w-0 flex-col gap-2" aria-labelledby={heading}>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <h3 id={heading} className="min-w-0 text-xs font-medium text-fg-secondary tabular-nums">
          {[
            t("citations.sourcesCount", { count: total }),
            unverified > 0 ? t("citations.verdicts.unverified", { count: unverified }) : null,
            unavailable > 0 ? t("citations.verdicts.unavailable", { count: unavailable }) : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </h3>
        {data.state === "checking" ? (
          <span className="text-xs text-fg-muted" aria-live="polite">
            {t("citations.checking")}
          </span>
        ) : null}
        <Button
          ref={toggle}
          type="button"
          variant="ghost"
          size="xs"
          className="ms-auto"
          aria-expanded={expanded}
          aria-controls={`sources-${messageId}`}
          onClick={() => setOpen(!expanded)}
        >
          {expanded ? t("citations.collapse") : t("citations.showAll")}
        </Button>
      </div>
      <div id={`sources-${messageId}`} className="min-w-0">
        {expanded ? (
          <div className="flex flex-col gap-3">
            {data.citations.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {data.web.length > 0 ? (
                  <h4 className="flex items-baseline gap-2 text-xs font-medium text-fg-muted">
                    {t("citations.projectSources")}
                    <span className="tabular-nums">{data.citations.length}</span>
                  </h4>
                ) : null}
                <ol className="flex flex-col divide-y divide-line border-y border-line text-sm">
                  {data.citations.map((c) => (
                    <SourceRow
                      key={c.handle}
                      anchor={sourceAnchor(messageId, c.handle)}
                      citation={c}
                      data={data}
                      projectId={projectId}
                      conversationId={props.conversationId}
                      canOpen={canOpen}
                    />
                  ))}
                </ol>
              </div>
            ) : null}
            {data.web.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <h4 className="flex items-baseline gap-2 text-xs font-medium text-fg-muted">
                  {t("citations.web.sources")}
                  <span className="tabular-nums">{data.web.length}</span>
                </h4>
                <ol className="flex flex-col divide-y divide-line border-y border-line text-sm">
                  {data.web.map((c) => (
                    <WebSourceRow
                      key={c.handle}
                      anchor={sourceAnchor(messageId, c.handle)}
                      citation={c}
                      data={data}
                    />
                  ))}
                </ol>
              </div>
            ) : null}
            {help.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <h4 className="flex items-baseline gap-2 text-xs font-medium text-fg-muted">
                  {t("citations.help.sources")}
                  <span className="tabular-nums">{help.length}</span>
                </h4>
                <ul className="flex min-w-0 flex-wrap items-center gap-1.5">
                  {help.map((c) => (
                    <li
                      key={c.handle}
                      id={sourceAnchor(messageId, c.handle)}
                      tabIndex={-1}
                      className={cn("flex min-w-0 max-w-full", SOURCE_ROW_CLASS)}
                    >
                      {props.chip(c.handle)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <ul className="flex min-w-0 flex-wrap items-center gap-1.5">
            {visible.map((h) => (
              <li key={h} className="flex min-w-0 max-w-full">
                {props.chip(h)}
              </li>
            ))}
            {folded > 0 ? (
              <li>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className="h-6 tabular-nums"
                  aria-expanded={false}
                  aria-label={t("citations.moreLabel", { count: folded })}
                  onClick={() => {
                    setOpen(true);
                    // The overflow control leaves with the strip; the toggle keeps the place.
                    setTimeout(() => toggle.current?.focus(), 0);
                  }}
                >
                  {t("citations.more", { count: folded })}
                </Button>
              </li>
            ) : null}
          </ul>
        )}
      </div>
      <p aria-live="polite" className="sr-only">
        {announce}
      </p>
    </section>
  );
}

function SourceRow(props: {
  anchor: string;
  conversationId: string | undefined;
  citation: RenderedCitation;
  data: CitationsData;
  projectId: string;
  canOpen: (documentId: string) => boolean;
}) {
  const { citation: c, data, projectId, canOpen } = props;
  const { t } = useTranslation();
  const attachmentContext = useAttachmentContext();
  const view = useSourceViewer();
  const openTarget = useCitationOpen(c, projectId, props.conversationId);
  const file =
    c.title !== null && c.attachment
      ? attachmentContext?.list?.attachments.find((a) => a.id === c.documentId)
      : undefined;
  const state = citationState(c, data);
  const provenance = c.title === null ? null : provenanceLabel(c.provenance);
  const flagged = c.title !== null && c.provenance?.flagged === true;
  return (
    <li
      id={props.anchor}
      tabIndex={-1}
      className={cn(
        "grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 gap-y-0.5 py-2",
        SOURCE_ROW_CLASS,
      )}
    >
      {/* A plain numeral: the state line beside it carries the verdict (no badge on badge). */}
      <span className="w-6 pt-px text-xs font-medium text-fg-muted tabular-nums">{c.handle}</span>
      {c.title === null ? (
        <span className="text-fg-muted italic">{t(`citations.state.${state}`)}</span>
      ) : (
        <span className="min-w-0">
          <span className="font-medium break-words text-fg">{c.title}</span>
          {c.attachment ? (
            <span className="text-xs text-fg-muted">
              {" "}
              · {t("attachments.marker")} · {t("attachments.path")}
            </span>
          ) : null}
          <span className="text-fg-muted"> · {locationLabel(c.location)}</span>
          {provenance ? (
            <span className={flagged ? "text-warning-fg" : "text-fg-muted"}> · {provenance}</span>
          ) : null}
          {c.newerVersion ? (
            <span className="text-fg-muted"> · {t("wiki.newerVersion")}</span>
          ) : null}
          {c.classification ? (
            <>
              {" "}
              <ClassificationMark id={c.classification} className="ms-1 -translate-y-px" />
            </>
          ) : null}
        </span>
      )}
      <span className="col-start-2 flex flex-wrap items-center gap-x-3 gap-y-0.5">
        {state === "unavailable" || state === "deleted" || state === "pending" ? null : (
          <span
            className={cn(
              "inline-flex items-center gap-1 text-xs font-medium",
              stateTextClass(state),
            )}
          >
            <StateIcon state={state} />
            {stateText(state, c)}
          </span>
        )}
        {c.title !== null ? (
          (
            c.attachment
              ? file?.status === "ready" && file.downloadAllowed && props.conversationId
              : c.kind === "wiki_page" || canOpen(c.documentId)
          ) ? (
            openTarget?.mode === "viewer" ? (
              <button
                type="button"
                onClick={() => view(openTarget.target)}
                className="text-xs font-medium text-accent-text underline-offset-4 hover:underline"
              >
                {t("citations.open")}
                <span className="sr-only"> {c.title}</span>
              </button>
            ) : (
              <a
                href={
                  openTarget?.mode === "tab"
                    ? openHrefOf(openTarget.target)
                    : citationHref(projectId, c, props.conversationId)
                }
                referrerPolicy="no-referrer"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-accent-text underline-offset-4 hover:underline"
              >
                {t("citations.open")}
                <span className="sr-only">
                  {" "}
                  {c.title} {t("app.newTab")}
                </span>
              </a>
            )
          ) : (
            <span className="text-xs text-fg-muted">{t("files.citeOnly")}</span>
          )
        ) : null}
      </span>
    </li>
  );
}

/**
 * A web result the answer cites: the "web" badge, the title, the domain and published date, the
 * check's verdict and a link that opens the page in a new tab (`http(s)` only).
 */
function WebSourceRow(props: { anchor: string; citation: WebCitation; data: CitationsData }) {
  const { citation: c, data } = props;
  const { t } = useTranslation();
  const state = webCitationState(c, data);
  const href = safeHref(c.url);
  const meta = webMeta(c.domain, c.publishedAt);
  return (
    <li
      id={props.anchor}
      tabIndex={-1}
      className={cn(
        "grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 gap-y-0.5 py-2",
        SOURCE_ROW_CLASS,
      )}
    >
      <span className="w-6 pt-px text-xs font-medium text-fg-muted tabular-nums">{c.handle}</span>
      <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <WebBadge />
        <span className="min-w-0 font-medium break-words text-fg">{c.title ?? c.domain}</span>
      </span>
      <span className="col-start-2 flex flex-wrap items-center gap-x-3 gap-y-0.5">
        {meta ? <span className="text-xs text-fg-muted tabular-nums">{meta}</span> : null}
        {state === "pending" ? null : (
          <span
            className={cn(
              "inline-flex items-center gap-1 text-xs font-medium",
              stateTextClass(state),
            )}
          >
            <StateIcon state={state} />
            {stateText(state, undefined)}
          </span>
        )}
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-xs font-medium text-accent-text underline-offset-4 hover:underline"
          >
            {t("citations.web.open")}
            <span className="sr-only">
              {" "}
              {c.title ?? c.domain} {t("app.newTab")}
            </span>
          </a>
        ) : null}
      </span>
    </li>
  );
}

function InstructionsMessage(props: {
  instructions: NonNullable<ReturnType<typeof parseInstructions>>;
}) {
  const { t } = useTranslation();
  const { items, title } = props.instructions;
  return (
    <article
      aria-label={t("citations.speaker.user")}
      className="flex max-w-[85%] flex-col gap-2 self-end rounded-2xl rounded-br-md bg-surface-selected/70 px-4 py-2.5"
    >
      <p className="flex items-center gap-1.5 text-[13px] text-fg-secondary">
        <Bot aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.75} />
        <span>
          {t("wiki.author.sent.title", { count: items.length })}
          <span className="sr-only">: {title}</span>
        </span>
      </p>
      <ul className="flex flex-col gap-2">
        {items.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a sent message never reorders.
          <li key={index} className="flex flex-col gap-0.5 text-[15px] leading-relaxed text-fg">
            <q className="w-fit rounded border border-dashed border-line-strong px-1.5 text-[13px] text-fg-secondary">
              {item.quote}
            </q>
            <span className="text-xs text-fg-muted">
              {t("wiki.author.sent.on", {
                where: [item.page, ...(item.section ? [item.section] : [])].join(" › "),
              })}
            </span>
            <span className="whitespace-pre-wrap">{item.text}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}
